// src/services/programVideos.js
// Data layer for PROGRAM VIDEOS recorded in-app via the pre-recorded broadcaster
// (MediaRecorder → IDB → Mux Direct Upload). See docs/decisions/adr-program-recorded-videos.md.
//
// These live in their OWN `programVideos` collection — deliberately NOT
// `classRecordings` — so a recorded program video can never leak into the public
// On Demand list (which queries classRecordings). Same isolation rationale as
// `series`. A program's videos can MIX these with Bunny-collection videos:
// `program.videoOrder[]` interleaves Bunny videoIds and programVideos doc ids
// (distinct string namespaces, so there's no collision).
//
// Doc schema:
//   {
//     programId,                  // owning program
//     status,                     // 'processing' | 'ready' | 'errored'
//     title, description,         // admin-authored (description = FormattedText markdown)
//     muxAssetId, muxPlaybackId,  // filled by api/mux-webhook.js when the asset is ready
//     durationSeconds, length,    // length = round(durationSeconds/60) for the card pill
//     order,                      // sort within the recorded set
//     createdAt
//   }
//
// The doc id is the STABLE video identifier used by `videoOrder[]` and by the
// completions event log (videoId = programVideos doc id) — it exists from
// creation, before Mux assigns an asset id, so ordering + completions are stable
// even while status === 'processing'.

import { db, firebaseEnabled } from './firebase';
import {
    collection, doc, getDocs, query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    addDoc, updateDoc, deleteDoc,
} from './firestoreWrite';

// All recorded videos for a program, ordered by `order` asc. Returns [] empty,
// null on failure / Firebase disabled (same contract as fetchPrograms).
export async function fetchProgramVideos(programId) {
    if (!firebaseEnabled || !programId) return null;
    try {
        const q = query(
            collection(db, 'programVideos'),
            where('programId', '==', programId),
            orderBy('order', 'asc'),
        );
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
        // A missing composite index (programId + order) would throw — fall back
        // to an unindexed equality query so the feature degrades to working
        // (just unsorted) rather than empty. Sort client-side.
        try {
            const q2 = query(collection(db, 'programVideos'), where('programId', '==', programId));
            const snap = await getDocs(q2);
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            list.sort((a, b) => (a.order || 0) - (b.order || 0));
            return list;
        } catch (e2) {
            console.error('fetchProgramVideos failed:', e2?.message || e2);
            return null;
        }
    }
}

// Create the placeholder doc at record time (status:'processing'). Returns the
// new doc id so the caller can hand it to the Mux Direct Upload passthrough; the
// webhook flips it to 'ready' with the asset/playback ids + duration.
export async function createProgramVideo(data) {
    if (!firebaseEnabled) return null;
    try {
        const ref = await addDoc(collection(db, 'programVideos'), {
            status: 'processing',
            ...data,
            createdAt: serverTimestamp(),
        });
        return ref.id;
    } catch (e) {
        console.error('createProgramVideo failed:', e?.message || e);
        return null;
    }
}

export async function updateProgramVideo(id, data) {
    if (!firebaseEnabled || !id) return false;
    try { await updateDoc(doc(db, 'programVideos', id), data); return true; }
    catch (e) { console.error('updateProgramVideo failed:', e?.message || e); return false; }
}

export async function deleteProgramVideo(id) {
    if (!firebaseEnabled || !id) return false;
    try { await deleteDoc(doc(db, 'programVideos', id)); return true; }
    catch (e) { console.error('deleteProgramVideo failed:', e?.message || e); return false; }
}

// Map a programVideos doc into the shape VideoPlayerModal expects for a Mux
// asset — identical to how Workouts shapes a class recording, so the existing
// player path plays it with no changes. `id` is the STABLE doc id (used by
// videoOrder + completions). `processing` videos carry no playback id yet.
export function shapeProgramVideo(pv) {
    return {
        id: pv.id,
        title: pv.title || 'Recorded workout',
        description: pv.description || '',
        length: pv.length || (pv.durationSeconds ? Math.round(pv.durationSeconds / 60) : 0),
        groups: Array.isArray(pv.muscleGroups) ? pv.muscleGroups : [],
        // Mux playback — mirrors a class recording so VideoPlayerModal uses mux-player.
        isClassRecording: true,
        isProgramVideo: true,            // marker for any program-specific UI
        muxPlaybackId: pv.muxPlaybackId || '',
        muxAssetId: pv.muxAssetId || '',
        durationSeconds: pv.durationSeconds || 0,
        status: pv.status || 'processing',
        thumbnailUrl: pv.thumbnailUrl || '',
        // VideoCard renders `driveThumbnail` (the class-recording convention), so
        // mirror the Mux thumbnail here — otherwise the admin-picked frame AND the
        // webhook-seeded default frame never show on the program card. Empty ⇒ card
        // falls back to the branded placeholder exactly as before.
        driveThumbnail: pv.thumbnailUrl || '',
    };
}
